import {
  COUNTRY_CODES,
  REGION_CHANGE_COOLDOWN_DAYS,
  billingCurrencyFor,
  type BillingAccountDto,
} from '@esportsbudget/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCw } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { api } from '@/lib/api';
import { billingOverviewKey, useBillingOverview } from '@/lib/billing';
import { errorMessage } from '@/lib/errorMessage';
import { useAuth } from '@/providers/AuthProvider';

/**
 * Обязательный выбор региона (TASK #32 PHASE 2, ужесточено в TASK #34
 * PHASE 4).
 *
 * Legacy-аккаунты (заведённые до появления биллинга) имеют
 * `BillingAccount.status = SETUP_REQUIRED`. Раньше приложение это никак не
 * замечало — раздел биллинга существовал, но выбрать регион в нём было
 * негде (см. историю `BillingTab`), так что такой аккаунт мог годами
 * работать без валюты расчётов.
 *
 * Инвариант простой и единственный: пока состояние аккаунта не подтверждено
 * как настроенное, дети (`AppRoutes` — всё остальное приложение) НЕ
 * рендерятся вовсе — не оверлей поверх смонтированного приложения, а
 * отсутствие его в DOM. Это относится не только к `SETUP_REQUIRED`, но и к
 * состоянию загрузки/ошибки самой проверки: fail-closed значит, что при
 * сомнении в статусе аккаунта приложение остаётся закрытым, а не
 * открывается по умолчанию.
 *
 * Пропущено для demo не отдельной веткой, а тем, что у demo-аккаунтов
 * регион выставляется прямо при создании (`server/src/modules/demo/service.ts`,
 * `server/src/modules/billing/service.ts` нормализует и старые demo без
 * региона) — `setupRequired` для demo просто никогда не бывает `true`.
 */
export function MandatoryRegionGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const { status } = useAuth();
  const overview = useBillingOverview();

  if (status !== 'authenticated') {
    return <>{children}</>;
  }

  if (overview.isLoading) {
    return (
      <BlockingScreen>
        <p className="text-[13px] text-muted" role="status" aria-live="polite">
          {t('loading')}
        </p>
      </BlockingScreen>
    );
  }

  if (overview.isError) {
    return (
      <BlockingScreen>
        <div className="flex flex-col items-center gap-3 text-center" role="alert">
          <p className="text-[14px] font-medium">{t('billing:gateCheckFailed', { ns: 'billing' })}</p>
          <Button icon={<RotateCw className="h-4 w-4" />} onClick={() => void overview.refetch()}>
            {t('retry')}
          </Button>
        </div>
      </BlockingScreen>
    );
  }

  if (overview.data?.setupRequired) {
    return <MandatoryRegionModal />;
  }

  return <>{children}</>;
}

/**
 * Полноэкранная блокирующая заглушка для состояний "загрузка" и "ошибка" —
 * до того, как известно, нужен ли gate вообще, показывать реальное
 * приложение нельзя (fail closed).
 */
function BlockingScreen({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-6">
      {children}
    </div>
  );
}

function MandatoryRegionModal() {
  const { t, i18n } = useTranslation(['billing', 'common', 'errors']);
  const queryClient = useQueryClient();
  const [countryCode, setCountryCode] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const countries = useMemo(() => {
    const names = new Intl.DisplayNames([i18n.language], { type: 'region' });
    return COUNTRY_CODES.map((code) => ({ value: code, label: names.of(code) ?? code })).sort((a, b) =>
      a.label.localeCompare(b.label, i18n.language),
    );
  }, [i18n.language]);

  const previewCurrency = countryCode ? billingCurrencyFor(countryCode) : null;

  const setupRegion = useMutation({
    mutationFn: () => api.post<BillingAccountDto>('/billing/setup-region', { countryCode }, { skipWorkspace: true }),
    onSuccess: (result) => {
      setSubmitError(null);
      // Обновляем кэш сразу — не ждём отдельного рефетча, чтобы окно закрылось
      // в тот же кадр, где сервер подтвердил новое состояние.
      queryClient.setQueryData(billingOverviewKey, result);
      void queryClient.invalidateQueries({ queryKey: billingOverviewKey });
    },
    onError: (error) => setSubmitError(errorMessage(error, t)),
  });

  return (
    <Modal
      open
      dismissible={false}
      busy={setupRegion.isPending}
      onClose={() => {
        /* окно не закрывается иначе, чем успешным сохранением региона */
      }}
      title={t('billing:mandatoryTitle')}
      description={t('billing:mandatoryDescription')}
      footer={
        <Button
          disabled={!countryCode || setupRegion.isPending}
          loading={setupRegion.isPending}
          onClick={() => setupRegion.mutate()}
        >
          {t('billing:mandatorySave')}
        </Button>
      }
    >
      <div className="space-y-3">
        <Select
          label={t('billing:regionSelectLabel')}
          placeholder={t('billing:regionSelectPlaceholder')}
          options={countries}
          value={countryCode}
          onChange={(event) => setCountryCode(event.target.value)}
        />

        {previewCurrency && (
          <p className="text-[13px] font-medium">
            {t('billing:accountCurrencyPreview', { currency: previewCurrency })}
          </p>
        )}

        <p className="text-[12.5px] text-subtle">
          {t('billing:mandatoryCooldownHint', { days: REGION_CHANGE_COOLDOWN_DAYS })}
        </p>

        {submitError && (
          <p role="alert" className="text-[12.5px] text-danger">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  );
}
